import { parsePhoneNumber } from 'react-phone-number-input'

import { IEditStoreFormData } from '@/components-latest/ui/AccountPage/EditStoreInfoForm'
import { IStoreProfileData } from '@/ducks/account/account.types'

export const mapStoreInfoToForm = (
  storeInfo: IStoreProfileData
): IEditStoreFormData => {
  const phones = storeInfo.phone

  return {
    email2: storeInfo.email2,
    store_img_name: storeInfo.store_img_name || '',
    avg_ratings: storeInfo.avg_ratings,
    address: storeInfo.address,
    email1: storeInfo.email1,
    facebook: storeInfo.facebook,
    genre: storeInfo.genre,
    inReview: storeInfo.inReview,
    support: storeInfo.support,
    store_name: storeInfo.store_name,
    website: storeInfo.website,
    phone1:
      phones.phone1?.number && phones.phone1?.country_code
        ? `${phones.phone1.country_code}${phones.phone1.number}`
        : null,
    phone2:
      phones.phone2?.number && phones.phone2?.country_code
        ? `${phones.phone2.country_code}${phones.phone2.number}`
        : null,
    phone3:
      phones.phone3?.number && phones.phone3?.country_code
        ? `${phones.phone3.country_code}${phones.phone3.number}`
        : null
  }
}

interface IEditStorePhone {
  number: string
  country_code: string
}

// Splits a combined E.164 string (e.g. "+213770195516") into the
// { number, country_code } shape the backend `edit_store_profile` intake
// expects. Returns undefined for empty/unparseable values so the caller can
// omit the field entirely rather than send a partial object.
const toRequestPhone = (
  phone: string | null | undefined
): IEditStorePhone | undefined => {
  if (!phone) return undefined

  try {
    // parsePhoneNumber throws (ParseError) on malformed input, and phone2/phone3
    // are user-controlled, so guard the whole parse and treat any failure as
    // "no valid phone" — the caller then omits the field.
    const parsed = parsePhoneNumber(phone)
    if (!parsed?.nationalNumber || !parsed?.countryCallingCode) return undefined

    return {
      number: parsed.nationalNumber,
      country_code: `+${parsed.countryCallingCode}`
    }
  } catch {
    return undefined
  }
}

// An empty `number` is how a cleared phone is expressed on the wire — the same
// convention the mobile app uses. The backend reads it as "remove this phone".
const CLEARED_PHONE: IEditStorePhone = { number: '', country_code: '' }

// Resolves a form phone field to what should be sent. This mirrors the backend
// contract: an absent key means "no opinion", an empty value means "remove".
//  - undefined (field not part of this update) -> undefined, key omitted
//  - null or "" (emptied by the user)          -> the cleared marker (removal)
//  - a valid number                            -> { number, country_code }
//  - non-empty but unparseable                 -> undefined, so the caller omits
//    the key and the stored value is left untouched (a typo must never wipe a
//    live phone)
const toRequestPhoneField = (
  phone: string | null | undefined
): IEditStorePhone | undefined => {
  if (phone === undefined) return undefined
  if (phone === null || phone === '') return CLEARED_PHONE
  return toRequestPhone(phone)
}

// Maps the edit-info form to the request body the backend intake reads.
// The backend destructures `email`, `first_phone`, `second_phone`,
// `third_phone` (objects) — not the flat `email1`/`phone1` form fields — and
// this is the same shape the mobile app already sends. The main phone (phone1)
// is intentionally read-only in the form, so it is never sent.
export const mapFormToEditStoreRequest = (
  formData: IEditStoreFormData,
  store_id: string
) => {
  const secondPhone = toRequestPhoneField(formData.phone2)
  const thirdPhone = toRequestPhoneField(formData.phone3)

  return {
    store_id,
    store_name: formData.store_name,
    email: {
      email1: formData.email1,
      // a removed second email is sent as "" (cleared), not null — null would be
      // skipped by the intake and the removal would be lost
      email2: formData.email2 ?? ''
    },
    ...(secondPhone ? { second_phone: secondPhone } : {}),
    ...(thirdPhone ? { third_phone: thirdPhone } : {}),
    store_img_name:
      typeof formData.store_img_name === 'string'
        ? formData.store_img_name
        : '',
    facebook: formData.facebook,
    website: formData.website,
    genre: formData.genre
  }
}
